import React, { useState, useEffect, useMemo } from 'react';
import { apiService } from '../services/api';
import { PlatformRegion } from '../types';

interface RegionUser {
  id: number;
  username: string;
  email: string;
  first_name: string;
  last_name: string;
}

const UserRegionAccess: React.FC = () => {
  const [allRegions, setAllRegions] = useState<PlatformRegion[]>([]);
  const [myRegions, setMyRegions] = useState<PlatformRegion[]>([]);
  const [allUsers, setAllUsers] = useState<RegionUser[]>([]);
  // regionId -> users who have access to that region
  const [regionUsersMap, setRegionUsersMap] = useState<Record<number, RegionUser[]>>({});
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Modal state
  const [selectedUser, setSelectedUser] = useState<RegionUser | null>(null);
  const [currentRegionIds, setCurrentRegionIds] = useState<number[]>([]);
  const [pendingRegionIds, setPendingRegionIds] = useState<number[]>([]);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const [allRegionsData, myRegionsData, allUsersData] = await Promise.all([
        apiService.getRegions(),
        apiService.getMyRegions(),
        apiService.getAllUsers(),
      ]);
      setAllRegions(allRegionsData);
      setMyRegions(myRegionsData);
      setAllUsers(allUsersData);

      // Fetch users for each region the current user has access to (to know current access state)
      const results = await Promise.all(
        myRegionsData.map(r =>
          apiService.listRegionUsers(r.id).then((data: any) => ({
            regionId: r.id,
            users: (data.users || []) as RegionUser[],
          }))
        )
      );

      const map: Record<number, RegionUser[]> = {};
      for (const result of results) {
        map[result.regionId] = result.users;
      }
      setRegionUsersMap(map);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load data');
    } finally {
      setIsLoading(false);
    }
  };


  const myRegionIds = useMemo(() => new Set(myRegions.map(r => r.id)), [myRegions]);

  const getUserRegionIds = (userId: number): number[] =>
    myRegions
      .filter(r => (regionUsersMap[r.id] || []).some(u => u.id === userId))
      .map(r => r.id);

  const openManageModal = (user: RegionUser) => {
    const currentIds = getUserRegionIds(user.id);
    setSelectedUser(user);
    setCurrentRegionIds(currentIds);
    setPendingRegionIds(currentIds);
  };

  const closeModal = () => {
    setSelectedUser(null);
    setCurrentRegionIds([]);
    setPendingRegionIds([]);
  };

  const toggleRegion = (regionId: number) => {
    if (!myRegionIds.has(regionId)) return;
    if (currentRegionIds.includes(regionId)) return; // already granted, no revoke
    setPendingRegionIds(prev =>
      prev.includes(regionId)
        ? prev.filter(id => id !== regionId)
        : [...prev, regionId]
    );
  };

  const handleSave = async () => {
    if (!selectedUser) return;
    setIsSaving(true);
    setError(null);
    try {
      const toGrant = pendingRegionIds.filter(id => !currentRegionIds.includes(id));

      await Promise.all(
        toGrant.map(regionId => apiService.grantRegionAccess(selectedUser.id, regionId))
      );

      setSuccessMessage(`Region access updated for ${selectedUser.username}`);
      closeModal();
      fetchData();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to update region access');
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="bg-white rounded-lg p-8 shadow-sm">
        <div className="text-xl text-gray-600">Loading...</div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg p-8 shadow-sm">
      <div className="mb-8 pb-5 border-b-2 border-gray-200">
        <h2 className="m-0 mb-1 text-slate-800 text-3xl font-semibold">User Region Access</h2>
        <p className="m-0 text-gray-500 text-base">
          Manage which regions each user can access
        </p>
      </div>

      {error && (
        <div className="bg-red-50 text-red-800 px-5 py-4 rounded border border-red-200 mb-5 flex justify-between items-center">
          {error}
          <button
            onClick={() => setError(null)}
            className="bg-transparent border-0 text-red-800 cursor-pointer font-medium underline"
          >
            Dismiss
          </button>
        </div>
      )}

      {successMessage && (
        <div className="bg-green-50 text-green-800 px-5 py-4 rounded border border-green-300 mb-5 flex justify-between items-center">
          {successMessage}
          <button
            onClick={() => setSuccessMessage(null)}
            className="bg-transparent border-0 text-green-800 cursor-pointer font-medium underline"
          >
            Dismiss
          </button>
        </div>
      )}

      <div className="overflow-x-auto rounded-lg shadow-sm">
        <table className="w-full border-collapse bg-white">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-4 text-left font-semibold text-slate-800 border-b-2 border-gray-200 text-sm">Username</th>
              <th className="px-4 py-4 text-left font-semibold text-slate-800 border-b-2 border-gray-200 text-sm">Name</th>
              <th className="px-4 py-4 text-left font-semibold text-slate-800 border-b-2 border-gray-200 text-sm">Email</th>
              <th className="px-4 py-4 text-left font-semibold text-slate-800 border-b-2 border-gray-200 text-sm">Regions</th>
              <th className="px-4 py-4 text-left font-semibold text-slate-800 border-b-2 border-gray-200 text-sm">Actions</th>
            </tr>
          </thead>
          <tbody>
            {allUsers.map(user => {
              const userRegionIds = getUserRegionIds(user.id);
              const userRegionNames = allRegions
                .filter(r => userRegionIds.includes(r.id))
                .map(r => r.name);
              return (
                <tr key={user.id} className="border-b border-gray-200 hover:bg-gray-50">
                  <td className="px-4 py-4 font-medium text-slate-800">{user.username}</td>
                  <td className="px-4 py-4 text-gray-500 text-sm">
                    {user.first_name || user.last_name
                      ? `${user.first_name} ${user.last_name}`.trim()
                      : '-'}
                  </td>
                  <td className="px-4 py-4 text-gray-500 text-sm">{user.email || '-'}</td>
                  <td className="px-4 py-4">
                    <div className="flex flex-wrap gap-1">
                      {userRegionNames.length > 0 ? (
                        userRegionNames.map(name => (
                          <span key={name} className="bg-blue-50 text-blue-700 px-2 py-0.5 rounded text-xs font-medium">
                            {name}
                          </span>
                        ))
                      ) : (
                        <span className="text-gray-400 text-xs">No access</span>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-4">
                    <button
                      className="bg-blue-600 text-white border-0 px-3 py-1.5 rounded cursor-pointer text-xs font-medium transition-colors hover:bg-blue-700"
                      onClick={() => openManageModal(user)}
                    >
                      Manage Regions
                    </button>
                  </td>
                </tr>
              );
            })}
            {allUsers.length === 0 && (
              <tr>
                <td colSpan={6} className="text-center py-10 text-gray-400">
                  No users found in your accessible regions
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Manage Regions Modal */}
      {selectedUser && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-[1000]"
          onClick={closeModal}
        >
          <div
            className="bg-white rounded-lg w-full max-w-md max-h-[80vh] overflow-y-auto shadow-xl"
            onClick={e => e.stopPropagation()}
          >
            <div className="flex justify-between items-center p-5 border-b border-gray-200">
              <div>
                <h3 className="m-0 text-slate-800 text-lg font-semibold">Manage Region Access</h3>
                <p className="m-0 text-sm text-gray-500 mt-0.5">{selectedUser.username}</p>
              </div>
              <button
                className="bg-transparent border-0 text-2xl cursor-pointer text-gray-400 hover:text-slate-800"
                onClick={closeModal}
              >
                &times;
              </button>
            </div>

            <div className="p-5">
              {allRegions.length === 0 ? (
                <div className="text-center py-8 text-gray-400">No regions available</div>
              ) : (
                <div className="flex flex-col gap-2">
                  {allRegions.map(region => {
                    const canManage = myRegionIds.has(region.id);
                    const alreadyGranted = currentRegionIds.includes(region.id);
                    const isChecked = pendingRegionIds.includes(region.id);
                    const isDisabled = !canManage || alreadyGranted;
                    return (
                      <label
                        key={region.id}
                        className={`flex items-center gap-3 p-3 rounded-lg border transition-colors ${
                          isDisabled
                            ? 'border-gray-100 bg-gray-50 cursor-not-allowed'
                            : 'border-gray-200 cursor-pointer hover:bg-gray-50'
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={isChecked}
                          disabled={isDisabled}
                          onChange={() => toggleRegion(region.id)}
                          className="w-4 h-4 accent-blue-600"
                        />
                        <div className="flex-1">
                          <div className="font-medium text-slate-800 text-sm">{region.name}</div>
                          <div className="text-xs text-gray-500">{region.environment}</div>
                        </div>
                        {alreadyGranted && (
                          <span className="text-xs text-green-600 font-medium">Access granted</span>
                        )}
                        {!canManage && !alreadyGranted && (
                          <span className="text-xs text-gray-400">No permission</span>
                        )}
                      </label>
                    );
                  })}
                </div>
              )}
            </div>

            <div className="flex justify-end gap-3 px-5 py-4 border-t border-gray-200">
              <button
                type="button"
                onClick={closeModal}
                disabled={isSaving}
                className="px-5 py-2.5 rounded cursor-pointer text-sm font-medium bg-gray-100 border border-gray-300 text-gray-700 hover:bg-gray-200 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSave}
                disabled={isSaving}
                className="px-5 py-2.5 rounded cursor-pointer text-sm font-medium bg-blue-600 text-white border-0 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSaving ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserRegionAccess;
